/**
 * Composable for generating EU warranty label SVGs
 * Replicates the TCI plugin's SVG generation logic
 */

const BRAND_AUTOFIT_THRESHOLD = 18
const BRAND_AUTOFIT_MAX = 30
const BRAND_HARD_LIMIT = 32

const MODEL_FONT_SIZE = 9.0
const MODEL_FIELD_WIDTH = 66.54
const MODEL_RIGHT_ALIGN_X = 263.29

interface SvgTemplates {
    master: string | null
    nested: string | null
}

const templates: SvgTemplates = {
    master: null,
    nested: null
}

export function useWarrantyLabelSvg() {
    async function loadTemplate(type: 'master' | 'nested'): Promise<string> {
        if (templates[type]) {
            return templates[type]!
        }

        const response = await fetch(`/warranty-labels/garan-${type}.svg`)
        if (!response.ok) {
            throw new Error(`Failed to load warranty label template: ${type}`)
        }

        const svg = await response.text()
        templates[type] = svg
        return svg
    }

    function uniquifyIds(svg: string): string {
        const suffix = '-' + Math.random().toString(36).substr(2, 8)

        // Replace id attributes and their references
        const idMatches = svg.match(/\bid="([^"]+)"/g)
        if (idMatches) {
            const ids = [...new Set(idMatches.map(m => m.match(/id="([^"]+)"/)![1]))]
            ids.forEach(id => {
                const newId = id + suffix
                svg = svg.replace(new RegExp(`id="${id}"`, 'g'), `id="${newId}"`)
                svg = svg.replace(new RegExp(`url\\(#${id}\\)`, 'g'), `url(#${newId})`)
                svg = svg.replace(new RegExp(`href="#${id}"`, 'g'), `href="#${newId}"`)
            })
        }

        // Replace Illustrator class names
        svg = svg.replace(/\bcls-\d+-[mn]\b/g, match => match + suffix)

        return svg
    }

    function replaceText(
        svg: string,
        anchor: string,
        value: string,
        fontSize?: number,
        rightAlignAtX?: number
    ): string {
        // Find text elements with the specified class
        const regex = new RegExp(
            `(<text[^>]*class="[^"]*tcinn-${anchor}[^"]*"[^>]*>)[\\s\\S]*?(<\\/text>)`,
            'g'
        )

        return svg.replace(regex, (match, opening, closing) => {
            let newOpening = opening

            // Update font-size if provided
            if (fontSize !== undefined) {
                if (newOpening.includes('font-size=')) {
                    newOpening = newOpening.replace(/font-size="[^"]*"/, `font-size="${fontSize}px"`)
                } else {
                    newOpening = newOpening.replace(/<text/, `<text font-size="${fontSize}px"`)
                }
            }

            // Update text-anchor and transform for right alignment
            if (rightAlignAtX !== undefined) {
                if (!newOpening.includes('text-anchor=')) {
                    newOpening = newOpening.replace(/<text/, '<text text-anchor="end"')
                } else {
                    newOpening = newOpening.replace(/text-anchor="[^"]*"/, 'text-anchor="end"')
                }

                // Update transform for right alignment
                const transformMatch = newOpening.match(/transform="translate\([\d.-]+\s+([\d.-]+)\)"/)
                if (transformMatch) {
                    const yValue = transformMatch[1]
                    newOpening = newOpening.replace(
                        /transform="translate\([^)]*\)"/,
                        `transform="translate(${rightAlignAtX} ${yValue})"`
                    )
                }
            }

            // Create tspan with the value
            return `${newOpening}\n      <tspan x="0" y="0">${escapeXml(value)}</tspan>\n    ${closing}`
        })
    }

    function escapeXml(text: string): string {
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&apos;')
    }

    function estimateTextWidth(text: string, fontSize: number): number {
        const narrow: Record<string, number> = {
            ' ': 0.27, 'i': 0.28, 'j': 0.28, 'l': 0.28, 'I': 0.31, 't': 0.33,
            'f': 0.33, 'r': 0.36, '.': 0.28, ',': 0.28, ':': 0.28, ';': 0.28,
            "'": 0.24, '!': 0.28, '|': 0.28, '-': 0.36
        }
        const wide: Record<string, number> = {
            'm': 0.83, 'w': 0.74, 'M': 0.80, 'W': 0.84
        }

        let em = 0
        for (const ch of text) {
            if (narrow[ch]) {
                em += narrow[ch]
            } else if (wide[ch]) {
                em += wide[ch]
            } else if (/[A-Z0-9]/.test(ch)) {
                em += 0.62
            } else {
                em += 0.52
            }
        }

        return em * fontSize
    }

    function autofitFontSize(length: number, threshold: number, max: number): number | undefined {
        if (length <= threshold) {
            return undefined
        }

        const base = 18.0
        const min = base * 0.6
        const span = Math.max(max - threshold, 1)
        const progress = Math.min((length - threshold) / span, 1.0)
        return base - (base - min) * progress
    }

    function truncate(brand: string): string {
        if (brand.length <= BRAND_HARD_LIMIT) {
            return brand
        }
        return brand.substring(0, BRAND_HARD_LIMIT - 1) + '…'
    }

    async function generateFullLabel(
        brand: string,
        modelIdentifier: string,
        years: number
    ): Promise<string> {
        let svg = await loadTemplate('master')
        svg = uniquifyIds(svg)

        // Ensure sans-serif fallback for all fonts
        svg = svg.replace(/font-family="[^"]*"/g, 'font-family="Inter, Arial, sans-serif"')

        brand = truncate(brand.trim())
        modelIdentifier = modelIdentifier.trim()

        // Replace years
        svg = replaceText(svg, 'years', String(years))

        // Replace model identifier with right alignment if it overflows
        const modelOverflows = estimateTextWidth(modelIdentifier, MODEL_FONT_SIZE) > MODEL_FIELD_WIDTH
        svg = replaceText(
            svg,
            'model',
            modelIdentifier,
            undefined,
            modelOverflows ? MODEL_RIGHT_ALIGN_X : undefined
        )

        // Replace brand with autofit font size
        const brandFontSize = autofitFontSize(brand.length, BRAND_AUTOFIT_THRESHOLD, BRAND_AUTOFIT_MAX)
        svg = replaceText(svg, 'brand', brand, brandFontSize)

        return svg
    }

    async function generateCompactLabel(years: number): Promise<string> {
        let svg = await loadTemplate('nested')
        svg = uniquifyIds(svg)

        // Ensure sans-serif fallback for all fonts
        svg = svg.replace(/font-family="[^"]*"/g, 'font-family="Inter, Arial, sans-serif"')

        // Replace years
        svg = replaceText(svg, 'years', String(years))

        return svg
    }

    return {
        generateFullLabel,
        generateCompactLabel
    }
}
